import { z } from 'zod';
import {
    CloverVoidOutcome,
    CloverVoidPaymentResult,
    CloverVoidReason,
    CloverVoidStatus,
    type CloverVoidCardTransactionBody,
    type CloverVoidErrorBody,
    type CloverVoidMalformedResponseDetails,
    type CloverVoidPaymentBody,
    type CloverVoidReferenceBody,
    type CloverVoidRejectedResponseDetails,
    type CloverCloudPayVoidRequestBody,
    type CloverCloudPayVoidRequestDetails,
    type CloverCloudPayVoidRequestHeaders,
    type CloverCloudPayVoidResponseBody,
    type CloverEcommerceVoidRequestDetails,
    type CloverEcommerceVoidResponseBody,
    type CloverVoidRequestDetails,
    type CloverVoidResponseBody,
    type CloverVoidResponseDetails,
    type CloverVoidSuccessfulResponseDetails,
} from '../../../types/ipg/clover/clover-void.types';

export const CloverVoidReasonSchema: z.ZodType<CloverVoidReason> = z.enum(CloverVoidReason);

export const CloverVoidStatusSchema: z.ZodType<CloverVoidStatus> = z.enum(CloverVoidStatus);

export const CloverVoidPaymentResultSchema: z.ZodType<CloverVoidPaymentResult> =
    z.enum(CloverVoidPaymentResult);

export const CloverCloudPayVoidRequestHeadersSchema: z.ZodObject<{
    deviceId: z.ZodString;
    idempotencyKey: z.ZodString;
    posId: z.ZodString;
    userAgent: z.ZodString;
    timeoutSeconds: z.ZodOptional<z.ZodNumber>;
}> = z.object({
    deviceId: z.string().min(1),
    idempotencyKey: z.string().min(1),
    posId: z.string().min(1),
    userAgent: z.string().min(1),
    timeoutSeconds: z.number().int().min(0).max(300).optional(),
}) satisfies z.ZodType<CloverCloudPayVoidRequestHeaders>;

export const CloverCloudPayVoidRequestBodySchema: z.ZodObject<{
    voidReason: typeof CloverVoidReasonSchema;
}> = z.object({
    voidReason: CloverVoidReasonSchema,
}) satisfies z.ZodType<CloverCloudPayVoidRequestBody>;

export const CloverCloudPayVoidRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/v1/payments/{paymentId}/void'>;
    paymentId: z.ZodString;
    headers: typeof CloverCloudPayVoidRequestHeadersSchema;
    body: typeof CloverCloudPayVoidRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/v1/payments/{paymentId}/void'),
    paymentId: z.string().min(13).max(13),
    headers: CloverCloudPayVoidRequestHeadersSchema,
    body: CloverCloudPayVoidRequestBodySchema,
}) satisfies z.ZodType<CloverCloudPayVoidRequestDetails>;

export const CloverEcommerceVoidRequestDetailsSchema = z.object({
    method: z.literal('POST'),
    path: z.literal('/v1/refunds'),
    chargeId: z.string().min(1),
    headers: z.object({ userAgent: z.string().min(1) }),
    body: z.object({ charge: z.string().min(1) }),
}) satisfies z.ZodType<CloverEcommerceVoidRequestDetails>;

export const CloverVoidRequestDetailsSchema: z.ZodType<CloverVoidRequestDetails> =
    z.discriminatedUnion('path', [
        CloverCloudPayVoidRequestDetailsSchema,
        CloverEcommerceVoidRequestDetailsSchema,
    ]);

export const CloverVoidReferenceBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1).max(13).optional(),
}) satisfies z.ZodType<CloverVoidReferenceBody>;

export const CloverVoidCardTransactionBodySchema: z.ZodObject<{
    authCode: z.ZodOptional<z.ZodString>;
    referenceId: z.ZodOptional<z.ZodString>;
    state: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    transactionNo: z.ZodOptional<z.ZodString>;
    type: z.ZodOptional<z.ZodString>;
}> = z.object({
    authCode: z.string().min(1).optional(),
    referenceId: z.string().min(1).optional(),
    state: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    transactionNo: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverVoidCardTransactionBody>;

export const CloverVoidPaymentBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    amount: z.ZodOptional<z.ZodNumber>;
    result: z.ZodOptional<typeof CloverVoidPaymentResultSchema>;
    cardTransaction: z.ZodOptional<typeof CloverVoidCardTransactionBodySchema>;
    externalPaymentId: z.ZodOptional<z.ZodString>;
    externalReferenceId: z.ZodOptional<z.ZodString>;
    refunds: z.ZodOptional<z.ZodArray<z.ZodUnknown>>;
    voidPaymentRef: z.ZodOptional<typeof CloverVoidReferenceBodySchema>;
    voidReason: z.ZodOptional<typeof CloverVoidReasonSchema>;
}> = z.object({
    id: z.string().min(1).max(13).optional(),
    amount: z.number().int().min(1).max(999999999).optional(),
    result: CloverVoidPaymentResultSchema.optional(),
    cardTransaction: CloverVoidCardTransactionBodySchema.optional(),
    externalPaymentId: z.string().min(1).max(32).optional(),
    externalReferenceId: z.string().min(1).max(12).optional(),
    refunds: z.array(z.unknown()).optional(),
    voidPaymentRef: CloverVoidReferenceBodySchema.optional(),
    voidReason: CloverVoidReasonSchema.optional(),
}) satisfies z.ZodType<CloverVoidPaymentBody>;

export const CloverCloudPayVoidResponseBodySchema: z.ZodObject<{
    payment: z.ZodOptional<typeof CloverVoidPaymentBodySchema>;
    paymentId: z.ZodString;
    voidReason: z.ZodOptional<typeof CloverVoidReasonSchema>;
    voidStatus: z.ZodOptional<typeof CloverVoidStatusSchema>;
}> = z.object({
    payment: CloverVoidPaymentBodySchema.optional(),
    paymentId: z.string().min(13).max(13),
    voidReason: CloverVoidReasonSchema.optional(),
    voidStatus: CloverVoidStatusSchema.optional(),
}) satisfies z.ZodType<CloverCloudPayVoidResponseBody>;

export const CloverEcommerceVoidResponseBodySchema: z.ZodType<CloverEcommerceVoidResponseBody> =
    z.object({
        id: z.string().min(1),
        object: z.literal('refund'),
        amount: z.number().int().min(1),
        charge: z.string().min(1),
        created: z.number().int().min(0).optional(),
        status: z.literal('succeeded'),
        metadata: z
            .object({
                refundType: z.string().min(1).optional(),
                authCode: z.string().min(1).optional(),
                refNum: z.string().min(1).optional(),
                gatewayResponseCode: z.string().min(1).optional(),
            })
            .optional(),
    });

export const CloverVoidResponseBodySchema: z.ZodType<CloverVoidResponseBody> = z.union([
    CloverCloudPayVoidResponseBodySchema,
    CloverEcommerceVoidResponseBodySchema,
]);

export const CloverVoidErrorBodySchema: z.ZodObject<{
    message: z.ZodString;
    type: z.ZodString;
    code: z.ZodOptional<z.ZodString>;
    requestId: z.ZodOptional<z.ZodString>;
    requestType: z.ZodOptional<z.ZodString>;
}> = z.object({
    message: z.string().min(1),
    type: z.string().min(1),
    code: z.string().min(1).optional(),
    requestId: z.string().min(1).optional(),
    requestType: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverVoidErrorBody>;

export const CloverVoidSuccessfulResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodEnum<{
        readonly VOIDED: typeof CloverVoidOutcome.VOIDED;
        readonly REFUNDED: typeof CloverVoidOutcome.REFUNDED;
    }>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CloverVoidResponseBodySchema;
}> = z.object({
    outcome: z.enum({
        VOIDED: CloverVoidOutcome.VOIDED,
        REFUNDED: CloverVoidOutcome.REFUNDED,
    }),
    httpStatus: z.literal(200),
    body: CloverVoidResponseBodySchema,
}) satisfies z.ZodType<CloverVoidSuccessfulResponseDetails>;

export const CloverVoidRejectedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverVoidOutcome.REJECTED>;
    httpStatus: z.ZodNumber;
    body: typeof CloverVoidErrorBodySchema;
}> = z.object({
    outcome: z.literal(CloverVoidOutcome.REJECTED),
    httpStatus: z.number().int().positive(),
    body: CloverVoidErrorBodySchema,
}) satisfies z.ZodType<CloverVoidRejectedResponseDetails>;

export const CloverVoidMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverVoidOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverVoidOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
}) satisfies z.ZodType<CloverVoidMalformedResponseDetails>;

export const CloverVoidResponseDetailsSchema: z.ZodType<CloverVoidResponseDetails> = z.union([
    CloverVoidSuccessfulResponseDetailsSchema,
    CloverVoidRejectedResponseDetailsSchema,
    CloverVoidMalformedResponseDetailsSchema,
]);
